import { useState, useCallback, useEffect } from 'react';
import { RoleDetail } from '../../../types/settings/RoleTypes';
import RoleDetailsView from '../../../components/Forms/RoleDetailsView';
import { getRoles } from '@/actions/data-fetch-roles';
import { toastMessage } from '@/utils/toast-message';
import ModernRolesTable from './ModernRolesTable';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { Shield } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface RoleClientWrapperProps {
    initialData: RoleDetail[];
    pagination?: PaginationProps;
}

export const RolesClientWrapper = ({ initialData, pagination }: RoleClientWrapperProps) => {
    const [selectedRole, setSelectedRole] = useState<RoleDetail | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [data, setData] = useState<RoleDetail[]>(initialData || []);
    const [currentPagination, setCurrentPagination] = useState<PaginationProps | undefined>(pagination);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

    const fetchData = useCallback(async (params: {
        page?: number,
        search_term?: string,
        sort_column?: string,
        sort_order?: 'ASC' | 'DESC'
    }={}) => {
        try {

            setIsLoading(true);
            const response = await getRoles(params);

            console.log(response.data.roles)

            if (response.data) {
                setData(response.data.roles || []);
                if (response.data.pagination) {
                    setCurrentPagination({
                        currentPage: response.data.pagination.currentPage,
                        totalPages: response.data.pagination.totalPages,
                        totalItems: response.data.pagination.totalItems,
                        itemsPerPage: response.data.pagination.itemsPerPage || 20,
                        hasNextPage: response.data.pagination.hasNextPage || false,
                        hasPreviousPage: response.data.pagination.hasPreviousPage || false
                    });
                }
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                toastMessage(error.message, "error"); // Accessing error.message when it's an instance of Error
            } else {
                toastMessage("An unknown error occurred", "error"); // Fallback for non-Error types
            }
        }
        finally {
            setIsLoading(false);
        }
    }, []);



     useEffect(() => {
            // Only fetch on initial load if no initial data
            if (!initialData || initialData.length === 0) {
                fetchData();
            }
        }, []);
    

    // View-only mode for master roles
    const handleEdit = (role?: RoleDetail) => {
        // Open modal in view-only mode to display role details
        setSelectedRole(role || null);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setSelectedRole(null);
    };

    // const handleOpenModal = (role?: RoleDetail) => {
    //     setSelectedRole(role || null);
    //     setIsModalOpen(true);
    // };

    // @ts-expect-error - Reserved for potential future role creation functionality
    const handleOpenModal = (role?: RoleDetail) => {
        // No creation for master roles - these are predefined
        return;
    };

    // @ts-expect-error - Reserved for potential future role update functionality
    const handleSuccess = async (updatedRole: RoleDetail) => {
        // This should not be called in view-only mode
        console.warn('handleSuccess called in view-only mode');
    };
    const handlePageChange = async (page: number) => {
        await fetchData({
            page,
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };
    // const handleSuccess = async(updatedRole:RoleDetail)=>
    // {
    //     setData(prevData=>
    //         prevData.map(role=>role.uuid===updatedRole.uuid?updatedRole:role)
    //     )
    // }


    const handleSearch = async (term: string): Promise<void> => {
        setSearchTerm(term);
        console.log(searchTerm);

        await fetchData({
            page: 1,
            search_term: term,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };


    const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
        setSortColumn(column);
        setSortOrder(order);
        await fetchData({
            page: currentPagination?.currentPage || 1,
            search_term: searchTerm,
            sort_column: column,
            sort_order: order
        });
    };

    return (
        <>
            <ModernSettingHeader
                title="Role Management"
                description="System-wide master roles with predefined permissions. These roles are consistent across all clients to ensure standardized access control."
                // buttonText="Create New Role"  // Commented out - no creation for master roles
                // onClick={() => handleOpenModal()}  // Commented out
                icon={<Shield className="w-6 h-6" />}
            />
            <div className="p-6">
                <ModernRolesTable
                    initialData={data}
                    onEdit={handleEdit}
                    pagination={currentPagination}
                    onPageChange={handlePageChange}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                />
            </div>




            {/* View-Only Modal for Master Roles */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleClose}
                title={selectedRole ? `${selectedRole.role_name} Details` : "Role Details"}
                subtitle="Master role permissions and access levels (read-only)"
                icon={<Shield className="w-6 h-6" />}
            >
                <RoleDetailsView
                    role={selectedRole}
                    onClose={handleClose}
                />
            </SettingsSlideModal>




        </>
    );
};

// export default RoleClientWrapper;